import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useRef } from "react";
import { useIsDesktop } from "../hooks/useMediaQuery";
import { Navbar, NavbarBrand, NavbarToggle, NavbarCollapse, NavbarLink} from 'flowbite-react';
import { HiOutlineLogout } from 'react-icons/hi';
import stablesIcon from '../resources/stables-icon.svg';
import stablesFull from '../resources/stables-full.svg';
import { getNavigationItems } from '../routes';
import { queryClient } from '../config/queryClient';


export const Navigation = ({ auth, setAuth, isSidebarOpen, setIsSidebarOpen, children }) => {
  const sidebarRef = useRef(null);
  // Only the md-breakpoint crossing matters for layout, so subscribe to that
  // media query rather than re-rendering the whole shell on every resize event.
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  // Get navigation items based on user type (and permission group for the
  // superadmin-only User Management entry)
  const navigationItems = getNavigationItems(auth.userType, auth.permissionGroup);
  
  // Helper function to check if route is active
  const isActiveRoute = (link) => {
    return location.pathname === link || location.pathname.startsWith(link + '/');
    
  };
  
  // Transform navigation items to include React elements
  const MENU_ITEMS = navigationItems.map(item => ({
    ...item,
    icon: React.createElement(item.icon, { size: item.size, className: item.className })
  }));
  // Desktop sidebar: `bottom` items (User Management, Settings) are pinned
  // above the sign-out block; everything else scrolls in the main list.
  const TOP_ITEMS = MENU_ITEMS.filter(item => !item.bottom);
  const BOTTOM_ITEMS = MENU_ITEMS.filter(item => item.bottom);

  const renderSidebarLink = (item) => {
    const isActive = isActiveRoute(item.link);
    return (
      <Link
        key={item.key}
        to={item.link}
        className={`flex items-center rounded-lg transition-colors py-3 ${
          isActive
            ? 'bg-[#E2F4E1] text-[#18668B]'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <span className="w-12 flex-shrink-0 flex justify-center">
          {item.icon}
        </span>
        <span className={`whitespace-nowrap overflow-hidden transition-[opacity,max-width] duration-150 ${
          isSidebarOpen ? 'opacity-100 max-w-xs delay-150' : 'opacity-0 max-w-0'
        } ${isActive ? 'font-semibold text-[#18668B]' : 'text-gray-700 dark:text-gray-300'}`}>
          {item.label}
        </span>
      </Link>
    );
  };

  // Derive display fields from auth (no API calls)
  const displayName = `${auth?.firstName} ${auth?.lastName}` || '';
  const displayEmail = auth?.email || '';

  const handleLogoutClick = () => {
    if (auth) {
      auth.cls.logout();
    }
    // Drop every cached query: reference lists (wholesalers, tenant access)
    // are per user and must not survive into the next sign-in (task 239 QA #7).
    queryClient.clear();
    navigate(location.pathname, { replace: true, state: null });
    setAuth({ ...auth, 'isLoggedIn': false });
  };

  const handleSidebarMouseEnter = () => setIsSidebarOpen(true);
  const handleSidebarMouseLeave = () => setIsSidebarOpen(false);

  const handleHomeClick = () => {
    navigate('/');
  };

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile Menu NavBar */}
      <Navbar fluid rounded className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 shadow-lg border-b border-gray-200 dark:border-gray-700">
        <NavbarBrand as={Link} to="/">
          <img src={stablesIcon} className="mx-2 h-8 w-8" alt="Stables" />
          <span className="self-center whitespace-nowrap text-lg font-semibold dark:text-white">Stables</span>
        </NavbarBrand>
        <div className="flex items-center gap-2">
          <NavbarToggle />
        </div>
        <NavbarCollapse>
          {MENU_ITEMS.map(item => {
            const isActive = isActiveRoute(item.link);
            return (
              <NavbarLink
                key={item.key}
                as={Link}
                to={item.link}
                className={`flex items-center gap-2 px-2 py-2 rounded transition-colors ${
                  isActive
                    ? 'bg-[#E2F4E1] text-[#18668B] font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </NavbarLink>
            );
          })}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleLogoutClick}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full flex items-center gap-2"
              title="Logout"
            >
              <HiOutlineLogout className="w-6 h-6" />
              <span>Logout</span>
            </button>
          </div>
        </NavbarCollapse>
      </Navbar>

      {/* Desktop Sidebar (custom, not Flowbite) */}
      <aside
        ref={sidebarRef}
        className={`hidden md:flex flex-col h-screen bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ${isSidebarOpen ? 'w-60' : 'w-20'} cursor-pointer fixed top-0 left-0 z-40`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <div className="flex items-center justify-center py-4" onClick={handleHomeClick}>
          {isSidebarOpen ? (
            <img src={stablesFull} className="h-20 w-auto" alt="Stables Full" />
          ) : (
            <img src={stablesIcon} className="h-20 w-10" alt="Stables Icon" />
          )}
        </div>
        <nav className="px-4">
          <div>
            {TOP_ITEMS.map(renderSidebarLink)}
          </div>
        </nav>
        {/* Spacer area */}
        <div className="flex-1" />
        {/* Pinned items (User Management for superadmins, Settings) above the border */}
        <div className="py-4 px-4">
          {BOTTOM_ITEMS.map(renderSidebarLink)}
        </div>
        <div className={`mt-auto p-4 border-t border-gray-200 dark:border-gray-700 ${isSidebarOpen ? '' : 'flex justify-center'}`}>
        {isSidebarOpen ? (
              <div
                onClick={handleLogoutClick}
                className="flex mx-6 items-center w-full rounded-lg transition-colors"
                title="Sign out"
              >
                <HiOutlineLogout className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                <div className="flex flex-col text-left ml-3 overflow-hidden">
                  <span className="font-semibold truncate dark:text-gray-300">{displayName}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{displayEmail}</span>
                </div>
              </div>
            ) : (
              <button
                onClick={handleLogoutClick}
                className="rounded-lg mx-6 my-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Logout"
              >
                <HiOutlineLogout className="w-6 h-6" />
              </button>
            )
          }
        </div>
      </aside>

      {/* Main Content */}
      <div
        className="flex-1 flex flex-col transition-all duration-300 w-full overflow-x-hidden"
        style={{
          marginLeft:
            isDesktop
              ? (isSidebarOpen ? '15rem' : '5rem')
              : 0,
          ['--sidebar-width']: isDesktop
            ? (isSidebarOpen ? '15rem' : '5rem')
            : '0'
        }}
      >
        <main className="flex-1 md:px-8 sm:px-4 px-2 py-16 md:py-8">
          {children}
        </main>
      </div>
    </div>
  );
};
